
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, Trash2, Users, RefreshCw } from 'lucide-react';
import { api } from '../services/api';
import { UnionStatementBatch } from '../types';
import UploadModal from './modals/UploadModal';
import ConfirmDialog from './modals/ConfirmDialog';

const BATCH_PAGE_SIZE = 15;

const UnionManagement: React.FC = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState<UnionStatementBatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [page, setPage] = useState(1);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [firstVisible, setFirstVisible] = useState<any>(null);
  const [isLastPage, setIsLastPage] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<any>({ isOpen: false });

  const fetchBatches = async (options: any = {}) => {
      setIsLoading(true);
      try {
        const result = await api.union.getPaginated({ orderByField: 'created_at', direction: 'desc', limitCount: BATCH_PAGE_SIZE, ...options });
        setBatches(result.data);
        setLastVisible(result.lastVisible);
        setFirstVisible(result.firstVisible);
        setIsLastPage(options.endBeforeDoc ? false : result.data.length < BATCH_PAGE_SIZE);
        setSelectedIds(new Set());
      } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };

  useEffect(() => { fetchBatches(); }, []);

  const handleNextPage = () => { if (lastVisible) { setPage(p => p + 1); fetchBatches({ startAfterDoc: lastVisible }); } };
  const handlePrevPage = () => { if (firstVisible) { setPage(p => p - 1); fetchBatches({ endBeforeDoc: firstVisible }); } };

  const handleDeleteSelected = () => {
      if (selectedIds.size === 0) return;
      setConfirmDialog({
          isOpen: true,
          title: `一括削除（${selectedIds.size}件）`,
          message: `選択した ${selectedIds.size} 件の明細書データを削除します。マスタデータには影響しません。よろしいですか？`,
          type: 'danger',
          onConfirm: async () => {
              setIsLoading(true);
              setConfirmDialog({ isOpen: false });
              try {
                  await api.union.deleteBatches(Array.from(selectedIds));
                  await fetchBatches();
              } catch (e) {
                  alert("削除に失敗しました。");
              } finally {
                  setIsLoading(false);
              }
          }
      });
  };

  const toggleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
      if(e.target.checked) setSelectedIds(new Set(batches.map(b => b.id)));
      else setSelectedIds(new Set());
  };

  const toggleSelectOne = (id: string) => {
      const next = new Set(selectedIds);
      if(next.has(id)) next.delete(id); else next.add(id);
      setSelectedIds(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-black text-gray-800 tracking-tight">組合明細管理</h2>
        <div className="flex gap-2">
            {selectedIds.size > 0 && (
                <button 
                    onClick={handleDeleteSelected}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-[13px] font-black shadow-sm active:scale-95 transition-all"
                >
                    <Trash2 size={14} /> 削除実行（{selectedIds.size}）
                </button>
            )}
            <button className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded text-[13px] font-black shadow-sm active:scale-95 transition-all" onClick={() => setIsUploadModalOpen(true)}><Plus size={14} /> 新規取込</button>
        </div>
      </div>

      <div className="bg-white rounded shadow-xs border border-gray-200 overflow-hidden">
        <div className="overflow-auto">
            <table className="w-full text-[13px] text-left border-separate border-spacing-0">
                <thead className="bg-gray-50 text-gray-600 font-black">
                    <tr>
                        <th className="p-2.5 w-10 text-center border-b sticky top-0 bg-gray-50 z-10">
                            <input 
                                type="checkbox" 
                                className="w-3.5 h-3.5" 
                                checked={batches.length > 0 && selectedIds.size === batches.length}
                                onChange={toggleSelectAll}
                            />
                        </th>
                        <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px]">組合名</th>
                        <th className="p-2.5 border-b sticky top-0 bg-gray-50 z-10 text-[13px]">対象月</th>
                        <th className="p-2.5 text-right border-b sticky top-0 bg-gray-50 z-10 text-[13px]">請求総額</th>
                        <th className="p-2.5 text-right border-b sticky top-0 bg-gray-50 z-10 text-[13px]">振込総額</th>
                        <th className="p-2.5 text-center border-b sticky top-0 bg-gray-50 z-10 text-[13px]">操作</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {isLoading ? (
                        <tr><td colSpan={6} className="p-12 text-center text-gray-400 font-bold">データを同期中...</td></tr>
                    ) : batches.length === 0 ? (
                        <tr><td colSpan={6} className="p-12 text-center text-gray-300 font-bold italic">登録されている明細書はありません</td></tr>
                    ) : batches.map((batch) => (
                        <tr key={batch.id} className={`hover:bg-blue-50/10 transition-colors ${selectedIds.has(batch.id) ? 'bg-blue-50/50' : ''}`}>
                            <td className="p-2.5 text-center border-b">
                                <input 
                                    type="checkbox" 
                                    className="w-3.5 h-3.5" 
                                    checked={selectedIds.has(batch.id)}
                                    onChange={() => toggleSelectOne(batch.id)}
                                />
                            </td>
                            <td className="p-2.5 font-bold text-gray-800 border-b text-[13px]">
                                <div className="flex items-center gap-1.5">
                                    <Users size={12} className={batch.union_type === 'satsuma' ? 'text-purple-400' : 'text-blue-400'} />
                                    {batch.union_type === 'satsuma' ? '薩摩砕石' : '南さつま'}
                                </div>
                            </td>
                            <td className="p-2.5 border-b font-bold text-gray-800 text-[13px]">{batch.year_month.replace('-', '/')}</td>
                            <td className="p-2.5 text-right font-black border-b text-gray-800 text-[13px]">¥{(batch.total_billing || 0).toLocaleString()}</td>
                            <td className="p-2.5 text-right font-black border-b text-gray-800 text-[13px]">¥{batch.total_deposit.toLocaleString()}</td>
                            <td className="p-2.5 text-center border-b">
                                <button onClick={() => navigate(`/union/${batch.id}`)} className="px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded font-bold text-[11px] shadow-xs hover:bg-gray-50">詳細表示</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <div className="flex justify-between items-center px-4 py-2 border-t bg-gray-50 text-[11px] text-gray-400">
            <span>全 {batches.length} 件</span>
            <div className="flex items-center gap-1.5">
                <button onClick={handlePrevPage} disabled={page <= 1} className="p-1 rounded border bg-white disabled:opacity-30"><ChevronLeft size={14}/></button>
                <span className="font-black text-gray-600 px-1">{page}</span>
                <button onClick={handleNextPage} disabled={isLastPage} className="p-1 rounded border bg-white disabled:opacity-30"><ChevronRight size={14}/></button>
            </div>
        </div>
      </div>

      <UploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} type="union" onSuccess={() => fetchBatches()} />
      <ConfirmDialog {...confirmDialog} onCancel={() => setConfirmDialog({isOpen: false})} />
    </div>
  );
};
export default UnionManagement;
